import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Popover from '@material-ui/core/Popover';
import { makeStyles } from '@material-ui/core/styles';
import SelectButton from '../SelectButton';

import {
  MenuItem,
  LeftDecorationContainer,
  styles,
  MenuList,
  SubText,
} from './Select.styled';

const anchorOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};

const useStyles = makeStyles(styles);

const Select = ({
  placeholder,
  options,
  value,
  allowEmpty,
  disabled,
  onChange,
  menuWidth,
  menuHeight,
}) => {
  const [open, setOpen] = useState(false);
  const [buttonWidth, setButtonWidth] = useState(undefined);
  const buttonRef = useRef();
  const classes = useStyles();

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    // Get the button width when the select is opened.
    // We don't ask for this in render as accessing clientWidth
    // causes a browser reflow.
    const newButtonWidth = buttonRef.current
      ? buttonRef.current.clientWidth
      : null;
    setOpen(true);
    setButtonWidth(newButtonWidth);
  };

  const handleChange = (selectedValue) => (event) => {
    handleClose();
    onChange(event, selectedValue);
  };

  const selectedOption = options.find((option) => option.value === value);

  return (
    <>
      <SelectButton
        disabled={disabled}
        onClick={handleOpen}
        ref={buttonRef}
        leftDecoration={selectedOption && selectedOption.leftDecoration}
        name={selectedOption && selectedOption.name}
        subText={selectedOption && selectedOption.subText}
        open={open}
        placeholder={placeholder}
      />
      <Popover
        anchorEl={buttonRef.current}
        anchorOrigin={anchorOrigin}
        getContentAnchorEl={null}
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.paper }}
        PaperProps={{
          style: {
            minWidth: buttonWidth || 'initial',
            height: menuHeight,
            width: menuWidth,
          },
        }}
      >
        <MenuList autoFocus>
          {allowEmpty && <MenuItem onClick={handleChange()}>None</MenuItem>}
          {options.map(
            ({
              value: itemValue,
              name,
              leftDecoration,
              isDisabled,
              subText,
            }) => {
              const handleClick = isDisabled
                ? undefined
                : handleChange(itemValue);

              return (
                <MenuItem
                  key={itemValue}
                  selected={itemValue === value}
                  onClick={handleClick}
                  disabled={isDisabled}
                  data-testid="menu-item"
                >
                  {leftDecoration && (
                    <LeftDecorationContainer>
                      {leftDecoration}
                    </LeftDecorationContainer>
                  )}
                  {name}
                  <SubText disabled={isDisabled}>{subText}</SubText>
                </MenuItem>
              );
            }
          )}
        </MenuList>
      </Popover>
    </>
  );
};

Select.propTypes = {
  disabled: PropTypes.bool,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allowEmpty: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      subText: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      leftDecoration: PropTypes.element,
    })
  ).isRequired,
  onChange: PropTypes.func,
  menuWidth: PropTypes.number,
  menuHeight: PropTypes.number,
};

Select.defaultProps = {
  disabled: false,
  value: undefined,
  allowEmpty: false,
  onChange: () => {},
  menuWidth: 406,
  menuHeight: 200,
};

export default Select;
